import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  Building2, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  MapPin,
  Navigation,
  History,
  User,
  Phone,
  Mail
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import usePersistentFilters from '../../hooks/usePersistentFilters';
import BlockVendorModal from '../../components/BlockVendorModal';
import VendorHistoryModal from '../../components/VendorHistoryModal';

const Vendors = () => {
  const { hasPermission, user, isAuthenticated } = useAuth();
  console.log('Vendors component - user:', user);
  console.log('Vendors component - isAuthenticated:', isAuthenticated);
  
  const defaultFilters = {
    search: '',
    isApproved: '',
    isBlocked: '',
    isActive: '',
    isDeleted: '',
    page: 1,
    limit: 10
  };

  const {
    filters,
    handleFilterChange,
    handlePageChange,
    clearFilters
  } = usePersistentFilters(defaultFilters, 'vendors-filters');
  
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyVendor, setHistoryVendor] = useState(null);

  const { data, isLoading, error, refetch } = useQuery(
    ['vendors', filters],
    async () => {
      console.log('Making API call with filters:', filters);
      console.log('Auth token:', localStorage.getItem('token'));
      const response = await api.get('/vendors', { params: filters });
      console.log('API response:', response);
      console.log('API response data:', response.data);
      return response.data;
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0
    }
  );

  console.log('Vendors component - data:', data);
  console.log('Vendors component - filters:', filters);
  console.log('Vendors component - isLoading:', isLoading);
  console.log('Vendors component - error:', error);
  console.log('Vendors component - data?.data:', data?.data);
  console.log('Vendors component - data?.data?.vendors:', data?.data?.vendors);

  const handleApproveVendor = async (vendorId) => {
    try {
      await api.put(`/vendors/${vendorId}/approve`);
      toast.success('Vendor approved successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to approve vendor');
    }
  };

  const handleBlockVendor = async (blockData) => {
    try {
      await api.put(`/vendors/${blockData.vendorId}/block`, blockData);
      toast.success(`Vendor ${blockData.isBlocked ? 'blocked' : 'unblocked'} successfully`);
      refetch();
    } catch (error) {
      toast.error('Failed to update vendor status');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) {
      return;
    }

    try {
      await api.delete(`/vendors/${vendorId}`);
      toast.success('Vendor deleted successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const handleToggleActive = async (vendorId) => {
    try {
      await api.put(`/vendors/${vendorId}/toggle-active`);
      toast.success('Vendor status updated successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to update vendor status');
    }
  };

  const getStatusBadge = (vendor) => {
    if (vendor.isBlocked) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Blocked
        </span>
      );
    }

    if (!vendor.isApproved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Pending Approval
        </span>
      );
    }

    if (!vendor.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Inactive
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  const vendors = data?.data?.vendors || [];
  const pagination = data?.data?.pagination;

  if (error) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Error Loading Vendors</h3>
        <p className="empty-state-description">
          {error.response?.data?.message || 'Failed to load vendors'}
        </p>
        <button onClick={() => refetch()} className="btn btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Vendors</h1>
              <p className="page-subtitle">
                Manage vendor accounts and approvals
              </p>
            </div>
            {hasPermission('vendors', 'write') && (
              <Link to="/vendors/create" className="btn btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Add Vendor
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title">Filters</h3>
            <button
              onClick={clearFilters}
              className="btn btn-outline btn-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vendors..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Approval Filter */}
            <div>
              <label className="form-label">Approval</label>
              <select
                value={filters.isApproved}
                onChange={(e) => handleFilterChange('isApproved', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="true">Approved</option>
                <option value="false">Pending</option>
              </select>
            </div>

            {/* Blocked Filter */}
            <div>
              <label className="form-label">Blocked</label>
              <select
                value={filters.isBlocked}
                onChange={(e) => handleFilterChange('isBlocked', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="true">Blocked</option>
                <option value="false">Active</option>
              </select>
            </div>

            {/* Active Filter */}
            <div>
              <label className="form-label">Active</label>
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            {/* Deleted Filter */}
            <div>
              <label className="form-label">Deleted</label>
              <select
                value={filters.isDeleted}
                onChange={(e) => handleFilterChange('isDeleted', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="true">Deleted</option>
                <option value="false">Not Deleted</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Building2 className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Vendors</p>
                <p className="text-2xl font-semibold text-gray-900">{vendors.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Approved</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {vendors.filter(v => v.isApproved).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {vendors.filter(v => !v.isApproved).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Blocked</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {vendors.filter(v => v.isBlocked).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Vendors ({pagination?.totalRecords || vendors.length})
            </h3>
            <button 
              onClick={() => {
                const csvContent = vendors.map(vendor => 
                  `${vendor.VendorName},${vendor.OwnerName},${vendor.ownerPhone},${vendor.ownerEmail},${vendor.city},${vendor.isApproved ? 'Approved' : 'Pending'}`
                ).join('\n');
                const blob = new Blob([`Vendor Name,Owner Name,Phone,Email,City,Status\n${csvContent}`], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'vendors.csv';
                a.click();
              }}
              className="btn btn-outline"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : vendors.length === 0 ? (
            <div className="empty-state">
              <Building2 className="empty-state-icon" />
              <h3 className="empty-state-title">No vendors found</h3>
              <p className="empty-state-description">
                {filters.search || filters.isApproved || filters.isBlocked || filters.isActive || filters.isDeleted
                  ? 'No vendors match your current filters.'
                  : 'Get started by adding your first vendor.'}
              </p>
              {hasPermission('vendors', 'write') && (
                <Link to="/vendors/create" className="btn btn-primary mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vendor
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Vendor</th>
                    <th className="table-header-cell">Owner</th>
                    <th className="table-header-cell">Contact</th>
                    <th className="table-header-cell">Location</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {vendors.map((vendor) => (
                    <tr key={vendor._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700">
                              {vendor.VendorName?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {vendor.VendorName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {vendor.fleetSysId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">{vendor.OwnerName}</div>
                        <div className="text-sm text-gray-500">{vendor.ownerEmail}</div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">{vendor.ownerPhone}</div>
                        <div className="text-sm text-gray-500">{vendor.ownerEmail}</div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm text-gray-900">
                              {vendor.city}, {vendor.state}
                            </div>
                            <div className="text-xs text-gray-500">
                              {vendor.country}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(vendor)}
                          {hasPermission('vendors', 'write') && vendor.isApproved && (
                            <button
                              onClick={() => handleToggleActive(vendor._id)}
                              className={vendor.isActive ? "text-warning-600 hover:text-warning-900" : "text-success-600 hover:text-success-900"}
                              title={vendor.isActive ? "Deactivate Vendor" : "Activate Vendor"}
                            >
                              {vendor.isActive ? (
                                <AlertCircle className="h-3 w-3" />
                              ) : (
                                <CheckCircle className="h-3 w-3" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/vendors/${vendor._id}`}
                            className="text-primary-600 hover:text-primary-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          
                          <button
                            onClick={() => {
                              setHistoryVendor(vendor);
                              setShowHistoryModal(true);
                            }}
                            className="text-secondary-600 hover:text-secondary-900"
                            title="View History"
                          >
                            <History className="h-4 w-4" />
                          </button>

                          {hasPermission('vendors', 'write') && !vendor.isApproved && (
                            <button
                              onClick={() => handleApproveVendor(vendor._id)}
                              className="text-success-600 hover:text-success-900"
                              title="Approve Vendor"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}

                          {hasPermission('vendors', 'write') && vendor.isApproved && (
                            <button
                              onClick={() => handleToggleActive(vendor._id)}
                              className={vendor.isActive ? "text-warning-600 hover:text-warning-900" : "text-success-600 hover:text-success-900"}
                              title={vendor.isActive ? "Deactivate Vendor" : "Activate Vendor"}
                            >
                              {vendor.isActive ? (
                                <AlertCircle className="h-4 w-4" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {hasPermission('vendors', 'write') && (
                            <button
                              onClick={() => {
                                setSelectedVendor(vendor);
                                setShowBlockModal(true);
                              }}
                              className={vendor.isBlocked ? "text-success-600 hover:text-success-900" : "text-warning-600 hover:text-warning-900"}
                              title={vendor.isBlocked ? "Unblock Vendor" : "Block Vendor"}
                            >
                              {vendor.isBlocked ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <XCircle className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {hasPermission('vendors', 'delete') && (
                            <button
                              onClick={() => handleDeleteVendor(vendor._id)}
                              className="text-error-600 hover:text-error-900"
                              title="Delete Vendor"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination && pagination.total > 1 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.current - 1)}
                  disabled={pagination.current <= 1}
                  className="btn btn-outline btn-sm"
                >
                  Previous
                </button>
                
                <span className="text-sm text-gray-700">
                  Page {pagination.current} of {pagination.total}
                </span>
                
                <button
                  onClick={() => handlePageChange(pagination.current + 1)}
                  disabled={pagination.current >= pagination.total}
                  className="btn btn-outline btn-sm"
                >
                  Next
                </button>
              </div>
              
              <div className="text-sm text-gray-500">
                {pagination.totalRecords} total records
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block Modal */}
      {showBlockModal && selectedVendor && (
        <BlockVendorModal
          isOpen={showBlockModal}
          vendor={selectedVendor}
          onClose={() => {
            setShowBlockModal(false);
            setSelectedVendor(null);
          }}
          onBlock={handleBlockVendor}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && historyVendor && (
        <VendorHistoryModal
          isOpen={showHistoryModal}
          vendorId={historyVendor._id}
          vendorName={historyVendor.VendorName}
          onClose={() => {
            setShowHistoryModal(false);
            setHistoryVendor(null);
          }}
        />
      )}
    </div>
  );
};

export default Vendors; 